import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { AnnualCostBarData, ChartColors, defaultColors } from '../types';
import { formatCurrency } from '../utils';

export interface AnnualCostChartProps {
  data: AnnualCostBarData[];
  colors?: ChartColors;
  className?: string;
}

export const AnnualCostChart: React.FC<AnnualCostChartProps> = ({
  data,
  colors = defaultColors,
  className = '',
}) => {
  const getBarColor = (index: number, isBaseline?: boolean): string => {
    if (isBaseline) return colors.baseline;
    if (index === 1) return colors.primary;
    return colors.alternatives[(index - 2) % colors.alternatives.length];
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip" style={{ 
          backgroundColor: '#fff', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p className="label" style={{ fontWeight: 'bold' }}>{data.label}</p>
          <p style={{ color: '#3b82f6' }}>
            Annual Cost: {formatCurrency(data.cost)}
          </p>
          {data.delta !== undefined && (
            <p style={{ color: data.delta < 0 ? '#10b981' : '#ef4444' }}>
              vs Baseline: {data.delta >= 0 ? '+' : ''}{formatCurrency(data.delta)}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`annual-cost-chart ${className}`}>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" />
          <YAxis
            label={{ value: 'Annual Cost (£)', angle: -90, position: 'insideLeft' }}
            tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar dataKey="cost" name="Annual Cost">
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(index, entry.isBaseline)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
